import { useEffect, useRef } from "react"
import { UseFormReturn, useWatch } from "react-hook-form"
import { useRegenerateReferralDocument } from "@/modules/patients/detail/progress-note/hooks/useRegenerateReferralDocument"
import { isNotForDoctorVisit } from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { Purpose } from "@/modules/patients/detail/progress-note/point-of-care/types"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { PurposeInput } from "@/modules/patients/services/ch/__generated__/chUpdateProgressNotePointOfCareMutation.graphql"
import { useUpdateProgressNotePointOfCareMutation } from "@/modules/patients/services/useUpdateProgressNotePointOfCareMutation"
import { Country } from "@/types/__generated__/union-types"

type UseAutoSubmitPointOfCareFormParameters = {
  form: UseFormReturn<PointOfCareFormType>
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  countryCode: Country
}

export const useAutoSubmitPointOfCareForm = ({
  form,
  episodeOfCareProgressNoteData,
  countryCode,
}: UseAutoSubmitPointOfCareFormParameters): void => {
  const { id: progressNoteId, pointOfCare } = episodeOfCareProgressNoteData
  const updateProgressNotePointOfCare = useUpdateProgressNotePointOfCareMutation(countryCode)
  const { regenerateReferralDocument } = useRegenerateReferralDocument({ progressNote: episodeOfCareProgressNoteData })

  const persistedCareProviderId =
    pointOfCare?.__typename === "ch_PointOfCare" ? (pointOfCare.careProvider?.id ?? null) : null
  const previousCareProviderIdRef = useRef<string | null>(persistedCareProviderId)

  const {
    control,
    formState: { isDirty },
    reset,
    handleSubmit,
  } = form

  const valueWatcher = useWatch({ control, compute: (formValues) => formValues })

  useEffect(() => {
    if (isDirty && valueWatcher) {
      handleSubmit((formData: PointOfCareFormType): void => {
        const purposeValues: Purpose[] = formData.purpose ?? []

        const purpose: PurposeInput = {
          laboratory: purposeValues.includes("laboratory"),
          physicalExam: purposeValues.includes("physicalExam"),
          instrumentalExamination: purposeValues.includes("instrumentalExamination"),
        }

        let doctorVisitValue = formData.isDoctorVisitAvoided
        if (!doctorVisitValue && formData.actualPointOfCare) {
          doctorVisitValue = isNotForDoctorVisit(formData.actualPointOfCare) ? null : false
        } else if (!doctorVisitValue && formData.medicalPointOfCare) {
          doctorVisitValue = isNotForDoctorVisit(formData.medicalPointOfCare) ? null : false
        }

        updateProgressNotePointOfCare(
          {
            id: progressNoteId,
            medicalPointOfCare: formData.medicalPointOfCare,
            actualPointOfCare: formData.actualPointOfCare || null,
            timeToTreatType: formData.timeToTreatType,
            reasonForChoosingActualType: formData.reasonForChoosingType,
            isDoctorVisitAvoided: doctorVisitValue,
            careProviderId: formData.careProviderId,
            purpose: purposeValues.length > 0 ? purpose : null,
            specialtyId: formData.specialtyId?.id,
            isBetterDocSuggestion: formData.isBetterDocSuggestion,
            resolvedBetterDocUsage: formData.resolvedBetterDocUsage ?? null,
          },
          () => {
            reset(formData, { keepIsSubmitted: true, keepDirtyValues: true })

            const newCareProviderId = formData.careProviderId ?? null
            if (newCareProviderId !== previousCareProviderIdRef.current) {
              regenerateReferralDocument()
            }
            previousCareProviderIdRef.current = newCareProviderId
          },
          () => reset(formData, { keepIsSubmitted: false, keepDirtyValues: true }),
        )
      })()
    }
  }, [
    handleSubmit,
    isDirty,
    progressNoteId,
    reset,
    updateProgressNotePointOfCare,
    regenerateReferralDocument,
    valueWatcher,
  ])
}
